import { useMemo } from 'react';
import { useRxData } from '@/providers/rxdb/rx-hooks';

interface DocumentWithName {
  id: string;
  name: string;
}

export const useUniqueDocument = (name: string, type: string, documentId?: string) => {
  const { result: data } = useRxData<DocumentWithName>(type, (collection) => {
    if (!name) {
      return undefined;
    }

    return collection.findOne({
      selector: {
        name: { $eq: name },
      },
    });
  });

  const hasError = useMemo(() => {
    const matchedDoc = data[0];
    // Ignore a retained stale match from a previous query (rx-hooks keeps the prior result
    // visible across a query swap); the $eq selector guarantees a real match has this exact name.
    if (matchedDoc && matchedDoc.name === name && name) {
      if (documentId) {
        // When editing, check if the matched document is a different document
        return matchedDoc.id !== documentId;
      }
      // When creating, any match is an error
      return true;
    }
    return false;
  }, [data, name, documentId]);

  const errorMessage = useMemo(
    () =>
      hasError ? (
        <span className="error-message">
          The name must be unique.
          <br />
          <b>{name}</b> is already in use.
        </span>
      ) : null,
    [hasError, name]
  );

  return {
    errorMessage,
    isUnique: !hasError,
  };
};
